import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  getPromotionRules,
  createPromotionRule,
  updatePromotionRule,
  deletePromotionRule,
  togglePromotionRule,
  getMenu,
} from '../services/api';
import { storage } from '../utils/storage';
import './PromotionsPage.css';

const MAX_RULES = 5;
const PROMO_TYPES = ['happy_hour', 'early_bird', 'off_peak', 'flash'];
const DISCOUNT_TYPES = ['percentage', 'fixed'];
const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const EMPTY_FORM = {
  name: '',
  type: 'happy_hour',
  days: [],
  startTime: '00:00',
  endTime: '23:59',
  discountType: 'percentage',
  discountValue: 10,
  categoryIds: [],
  itemIds: [],
  isActive: true,
};

function PromotionsPage() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [restaurantId, setRestaurantId] = useState(storage.get('selectedRestaurantId') || '');

  useEffect(() => {
    const handleRestaurantChange = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    window.addEventListener('storage', handleRestaurantChange);
    return () => {
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
      window.removeEventListener('storage', handleRestaurantChange);
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [rulesData, menuResponse] = await Promise.all([getPromotionRules(), getMenu()]);
      setRules(rulesData.rules || []);
      setMenuData(menuResponse.menu || []);
    } catch (err) {
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeCount = rules.filter((r) => r.isActive).length;

  const handleOpenCreate = () => {
    setEditingRule(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const handleOpenEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || '',
      type: rule.type || 'happy_hour',
      days: rule.days || [],
      startTime: rule.startTime || '00:00',
      endTime: rule.endTime || '23:59',
      discountType: rule.discountType || 'percentage',
      discountValue: rule.discountValue || 10,
      categoryIds: rule.categoryIds || [],
      itemIds: rule.itemIds || [],
      isActive: rule.isActive !== false,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      if (editingRule) {
        await updatePromotionRule(editingRule.id, form);
      } else {
        await createPromotionRule(form);
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving promotion rule:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      await togglePromotionRule(rule.id, !rule.isActive);
      await loadData();
    } catch (err) {
      console.error('Error toggling rule:', err);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await deletePromotionRule(ruleId);
      setShowDeleteConfirm(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  const handleDayToggle = (day) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleItemToggle = (itemId) => {
    setForm((prev) => ({
      ...prev,
      itemIds: prev.itemIds.includes(itemId)
        ? prev.itemIds.filter((id) => id !== itemId)
        : [...prev.itemIds, itemId],
    }));
  };

  const getPromoTypeLabel = (type) => t(`promotions.types.${type}`);
  const getDayLabel = (day) => t(`promotions.days.${day}`);

  if (loading) {
    return (
      <div className="page-container">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="promotions-header">
        <div>
          <h1>{t('promotions.title')}</h1>
          <span className="promotions-limit">
            {t('promotions.activeLimit', { count: activeCount, max: MAX_RULES })}
          </span>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleOpenCreate}
          disabled={activeCount >= MAX_RULES}
        >
          <Plus size={18} />
          {t('promotions.addRule')}
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="promotions-empty">
          <p>{t('promotions.empty')}</p>
        </div>
      ) : (
        <div className="promotions-list">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`promotions-card ${!rule.isActive ? 'promotions-card--inactive' : ''}`}
            >
              <div className="promotions-card__header">
                <div className="promotions-card__info">
                  <h3 className="promotions-card__name">{rule.name}</h3>
                  <span className={`promotions-card__type promotions-card__type--${rule.type}`}>
                    {getPromoTypeLabel(rule.type)}
                  </span>
                </div>
                <div className="promotions-card__actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleToggle(rule)}
                    title={rule.isActive ? t('promotions.deactivate') : t('promotions.activate')}
                  >
                    {rule.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleOpenEdit(rule)}
                    title={t('common.edit')}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowDeleteConfirm(rule.id)}
                    title={t('common.delete')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="promotions-card__details">
                <div className="promotions-card__detail">
                  <span className="promotions-card__label">{t('promotions.schedule')}</span>
                  <span>
                    {(rule.days || []).map((d) => getDayLabel(d)).join(', ')} &middot;{' '}
                    {rule.startTime} - {rule.endTime}
                  </span>
                </div>
                <div className="promotions-card__detail">
                  <span className="promotions-card__label">{t('promotions.discount')}</span>
                  <span>
                    {rule.discountType === 'percentage'
                      ? `${rule.discountValue}%`
                      : `$${rule.discountValue}`}
                  </span>
                </div>
              </div>

              {/* Delete confirmation */}
              {showDeleteConfirm === rule.id && (
                <div className="promotions-card__delete-confirm">
                  <p>{t('promotions.deleteConfirm')}</p>
                  <div className="promotions-card__delete-actions">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowDeleteConfirm(null)}
                    >
                      {t('common.cancel')}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(rule.id)}>
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRule ? t('promotions.editRule') : t('promotions.addRule')}</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="modal-body">
              {/* Name */}
              <div className="form-group">
                <label>{t('promotions.ruleName')}</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('promotions.ruleNamePlaceholder')}
                />
              </div>

              {/* Type */}
              <div className="form-group">
                <label>{t('promotions.promoType')}</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                >
                  {PROMO_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {getPromoTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Days of week */}
              <div className="form-group">
                <label>{t('promotions.daysOfWeek')}</label>
                <div className="promotions-days">
                  {DAYS_OF_WEEK.map((day) => (
                    <label key={day} className="promotions-day-checkbox">
                      <input
                        type="checkbox"
                        checked={form.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                      <span>{getDayLabel(day)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Time range */}
              <div className="form-group form-group--row">
                <div className="form-group">
                  <label>{t('promotions.startTime')}</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>{t('promotions.endTime')}</label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                  />
                </div>
              </div>

              {/* Discount */}
              <div className="form-group form-group--row">
                <div className="form-group">
                  <label>{t('promotions.discountType')}</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm((prev) => ({ ...prev, discountType: e.target.value }))}
                  >
                    {DISCOUNT_TYPES.map((dt) => (
                      <option key={dt} value={dt}>
                        {t(`promotions.discountTypes.${dt}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('promotions.discountValue')}</label>
                  <input
                    type="number"
                    min="0"
                    value={form.discountValue}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        discountValue: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Apply to: categories and items */}
              <div className="form-group">
                <label>{t('promotions.applyTo')}</label>
                <div className="promotions-apply-to">
                  {menuData.map((category) => (
                    <div key={category.categoryId} className="promotions-category-group">
                      <label className="promotions-category-checkbox">
                        <input
                          type="checkbox"
                          checked={form.categoryIds.includes(category.categoryId)}
                          onChange={() => handleCategoryToggle(category.categoryId)}
                        />
                        <strong>{category.name}</strong>
                      </label>
                      {!form.categoryIds.includes(category.categoryId) && category.items && (
                        <div className="promotions-items-list">
                          {category.items.map((item) => (
                            <label key={item.itemId} className="promotions-item-checkbox">
                              <input
                                type="checkbox"
                                checked={form.itemIds.includes(item.itemId)}
                                onChange={() => handleItemToggle(item.itemId)}
                              />
                              <span>{item.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={handleCloseModal}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
              >
                {saving ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsPage;
